import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { marked } from 'marked';

import { CircularProgress } from '@mui/material';

import { MakeToast } from '../../../../../../components';
import { NavCollapsedContext } from '../../../../../../utils/context';
import { CancelText, SubmitText } from '../../data/constants';
import { ContentContainerProps } from '../chat-screen.props';
import {
  FILE_UPLOAD_MESSAGE_MARKER,
  findLatestFileUploadMessageIndex,
} from '../../../lib/file-upload-utils';
import { AvatarLogo, CopyIcon, SwitchIcon, TextFile, Tick } from '../../../../../../assets/images';
import { truncateData } from '../../../../../../services';
import { DataItem, GeneratedFile, ToolCall } from '../../../../../../utils/interface-types';
import { downloadFileInChat } from '../../../services/chat';
import ChatMarkdown from '../../../components/chat-markdown';
import AssistantMessage from '../../../components/chat-ui/assistant-message';
import ChatThreadLayout from '../../../components/chat-ui/chat-thread-layout';
import UserMessageBubble from '../../../components/chat-ui/user-message-bubble';
import { toolCallHasVisualOutput } from '../../../lib/mcp-tool-utils';
import { scrollToBottomIfNearEnd, useStickToBottom } from '../../../hooks/use-stick-to-bottom';
import GrigoAssistantUiThreadPoc from '../../../components/assistant-ui-poc/grigo-assistant-ui-thread-poc';
import { isChatAssistantUiPocEnabled } from '../../../lib/chat-render-path';
import './content-container.css';

marked.setOptions({
  gfm: true,
  breaks: true,
});

const getMessageVersions = (message: DataItem['message']): string[] => {
  if (message == null) return [];
  if (Array.isArray(message)) {
    return message.filter((part): part is string => typeof part === 'string');
  }
  return typeof message === 'string' ? [message] : [];
};

const getLatestVersionIndex = (versions: string[]) => Math.max(versions.length - 1, 0);

function ContentContainer({
  chatScreenRef,
  chatScreenLoading,
  state,
  dispatch,
  sendButtonRef,
  activeProvider,
  onRetryGeneration,
}: ContentContainerProps) {
  const { isNavCollapsed } = useContext(NavCollapsedContext);
  const divRef = useRef<HTMLTextAreaElement>(null);
  const lastHumanMessageRef = useRef<HTMLDivElement>(null);
  const lastAIMessageRef = useRef<HTMLDivElement>(null);
  const [expandedThinking, setExpandedThinking] = useState<Set<number>>(new Set());
  const thinkingLiveRef = useRef<Set<number>>(new Set());
  const [expandedToolCalls, setExpandedToolCalls] = useState<Set<number>>(new Set());
  const [responseVersionByIndex, setResponseVersionByIndex] = useState<Record<number, number>>({});
  const [existingChat, setExistingChat] = useState<DataItem[]>([]);

  // Auto-expand tool output panel when widgets or images arrive
  useEffect(() => {
    existingChat.forEach((chat, idx) => {
      if (chat.role !== 'AI') return;
      const shouldExpand = chat.toolCalls?.some((tc) => toolCallHasVisualOutput(tc));
      if (shouldExpand) {
        setExpandedToolCalls((prev) => {
          if (prev.has(idx)) return prev;
          const next = new Set(prev);
          next.add(idx);
          return next;
        });
      }
    });
  }, [existingChat]);

  // Show thoughts while they stream; collapse when the answer starts (ChatGPT-style).
  useEffect(() => {
    const nowThinking = new Set<number>();
    existingChat.forEach((chat, idx) => {
      if (chat.role === 'AI' && chat.isThinking) nowThinking.add(idx);
    });
    setExpandedThinking((prev) => {
      const next = new Set(prev);
      let changed = false;
      nowThinking.forEach((idx) => {
        if (!thinkingLiveRef.current.has(idx) && !next.has(idx)) {
          next.add(idx);
          changed = true;
        }
      });
      thinkingLiveRef.current.forEach((idx) => {
        if (!nowThinking.has(idx) && next.has(idx)) {
          next.delete(idx);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    thinkingLiveRef.current = nowThinking;
  }, [existingChat]);
  const previousChatLengthRef = useRef<number>(0);
  const hasInitialScrolledRef = useRef<boolean>(false);

  const uploadingFileMessageIndex = useMemo(() => {
    if (!state.isFileUploadPending) return -1;
    return findLatestFileUploadMessageIndex(state.chatHistory);
  }, [state.chatHistory, state.isFileUploadPending]);

  useEffect(() => {
    setExistingChat(state.chatHistory);
  }, [state.chatHistory]);

  useEffect(() => {
    setResponseVersionByIndex((prev) => {
      const next = { ...prev };
      state.chatHistory.forEach((chat, idx) => {
        if (chat.role !== 'AI') return;
        const versions = getMessageVersions(chat.message);
        if (versions.length <= 1) return;
        const latestIndex = getLatestVersionIndex(versions);
        if (next[idx] === undefined || next[idx] >= versions.length) {
          next[idx] = latestIndex;
        }
      });
      return next;
    });
  }, [state.chatHistory]);

  const getSelectedVersionIndex = (messageIndex: number, versions: string[]) => {
    const stored = responseVersionByIndex[messageIndex];
    if (stored !== undefined && stored >= 0 && stored < versions.length) {
      return stored;
    }
    return getLatestVersionIndex(versions);
  };

  const setSelectedVersionIndex = (messageIndex: number, versionIndex: number) => {
    setResponseVersionByIndex((prev) => ({ ...prev, [messageIndex]: versionIndex }));
  };

  // Initialize previous chat length on mount
  useEffect(() => {
    previousChatLengthRef.current = state.chatHistory.length;
  }, []);

  // Scroll to bottom on initial load/refresh when chat history exists
  useEffect(() => {
    // Reset flag when chat history is cleared
    if (state.chatHistory.length === 0) {
      hasInitialScrolledRef.current = false;
      return undefined;
    }

    if (
      !hasInitialScrolledRef.current &&
      state.chatHistory.length > 0 &&
      chatScreenRef?.current &&
      !chatScreenLoading
    ) {
      // Wait for DOM to render, then scroll to bottom
      const scrollElement = chatScreenRef.current;
      const timeoutId = setTimeout(() => {
        if (scrollElement) {
          scrollElement.scrollTop = scrollElement.scrollHeight;
          hasInitialScrolledRef.current = true;
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [state.chatHistory, chatScreenRef, chatScreenLoading]);

  const lastHistoryMessage = state.chatHistory[state.chatHistory.length - 1];
  const isNewChatMessage = state.chatHistory.length > previousChatLengthRef.current;
  const forceScrollFromHuman =
    isNewChatMessage && lastHistoryMessage?.role === 'Human' && state.chatHistory.length > 0;

  useEffect(() => {
    previousChatLengthRef.current = state.chatHistory.length;
  }, [state.chatHistory.length]);

  useStickToBottom({
    scrollRef: chatScreenRef,
    deps: [state.chatHistory, existingChat],
    forceScroll: forceScrollFromHuman,
  });

  useEffect(() => {
    const onStreamLayoutTick = () => {
      scrollToBottomIfNearEnd(chatScreenRef);
    };
    window.addEventListener('grigo-stream-layout-tick', onStreamLayoutTick);
    return () => {
      window.removeEventListener('grigo-stream-layout-tick', onStreamLayoutTick);
    };
  }, [chatScreenRef]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = () => {
    if (divRef.current) {
      const textarea = divRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight, but cap at maxHeight
      const maxHeight = 200; // maxHeight from style
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      // If content exceeds maxHeight, show scrollbar
      if (textarea.scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.overflowY = 'hidden';
      }
    }
  };

  // Auto-resize textarea when edit mode is activated or content changes
  useEffect(() => {
    if (state.showEditQueryActions && divRef.current) {
      adjustTextareaHeight();
    }
  }, [state.showEditQueryActions, state.updateQuery]);

  // Add copy buttons to code blocks after render
  useEffect(() => {
    if (!chatScreenRef?.current) return;

    const codeBlocks = chatScreenRef.current.querySelectorAll('pre');

    codeBlocks.forEach((pre) => {
      const preElement = pre as HTMLElement;
      // Already enhanced: wrapper holds the button as sibling of <pre> so the button does not scroll with code
      if (preElement.parentElement?.getAttribute('data-chat-code-copy') === 'true') return;

      // Skip if code block is in human message, file message, thinking section, or tools section
      const messageContainer = pre.closest('.message-container, .response-container');
      const thinkingContainer = pre.closest('.thinking-container');
      const toolsContainer = pre.closest('.tools-container');
      if (
        messageContainer?.getAttribute('data-role') === 'Human' ||
        pre.closest('.message-span')?.textContent?.includes(FILE_UPLOAD_MESSAGE_MARKER) ||
        thinkingContainer ||
        toolsContainer
      ) {
        return;
      }
      const codeElement = preElement.querySelector('code');
      const hasContent = (codeElement?.textContent ?? preElement.textContent ?? '').trim();
      if (!hasContent) return;

      const wrap = document.createElement('div');
      wrap.className = 'chat-code-block-wrap';
      wrap.setAttribute('data-chat-code-copy', 'true');

      const parent = preElement.parentNode;
      if (!parent) return;
      parent.insertBefore(wrap, preElement);
      wrap.appendChild(preElement);

      const getCopyText = (): string => {
        const code = preElement.querySelector('code');
        const raw = code?.textContent ?? preElement.textContent ?? '';
        return raw.replace(/\u00a0/g, ' ').trim();
      };

      const copyBtn = document.createElement('button');
      copyBtn.type = 'button';
      copyBtn.className = 'code-copy-btn p-2';
      copyBtn.setAttribute('aria-label', 'Copy code');
      copyBtn.innerHTML = `<img src="${CopyIcon}" alt="" style="width: 12px; height: 12px; margin-top: 0.5em; margin-bottom: 0.5em;" /> <span class="code-copy-btn-label">Copy</span>`;

      copyBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const text = getCopyText();
        if (!text) {
          MakeToast('error', 'Nothing to copy');
          return;
        }
        try {
          await navigator.clipboard.writeText(text);
          copyBtn.innerHTML = `<img src="${Tick}" alt="" style="width: 12px; height: 12px; margin-top: 0.5em; margin-bottom: 0.5em;" /><span class="code-copy-btn-label code-copy-btn-label--ok">Copied!</span>`;
          setTimeout(() => {
            copyBtn.innerHTML = `<img src="${CopyIcon}" alt="" style="width: 12px; height: 12px; margin-top: 0.5em; margin-bottom: 0.5em;" /><span class="code-copy-btn-label">Copy</span>`;
          }, 2000);
        } catch {
          MakeToast('error', 'Failed to copy code');
        }
      });

      wrap.appendChild(copyBtn);
    });
  }, [existingChat, chatScreenRef]);

  const buildClipboardHTML = (markdown: string) => {
    const html = marked(markdown);

    return `
  <div style="
    font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,Cantarell;
    font-size: 14px;
    line-height: 1.6;
    color: #111827;
  ">
    ${(html as string)
      .replace(/<h1>/g, '<h1 style="font-size:20px;margin:16px 0 8px">')
      .replace(/<h2>/g, '<h2 style="font-size:18px;margin:14px 0 8px">')
      .replace(/<h3>/g, '<h3 style="font-size:16px;margin:12px 0 6px">')
      .replace(/<p>/g, '<p style="margin:8px 0">')
      .replace(/<ul>/g, '<ul style="margin:8px 0 8px 20px">')
      .replace(/<li>/g, '<li style="margin:4px 0">')
      .replace(/<hr>/g, '<hr style="margin:16px 0;border:none;border-top:1px solid #e5e7eb">')
      .replace(/<code>/g, '<code style="background:#f3f4f6;padding:2px 4px;border-radius:4px">')
      .replace(
        /<pre>/g,
        '<pre style="background:#111827;color:#f9fafb;padding:12px;border-radius:8px;overflow:auto">',
      )}
  </div>
  `;
  };

  const handleCopyToClipboard = async (markdown: string, index: number) => {
    try {
      const htmlContent = buildClipboardHTML(markdown);

      const textContent = markdown; // fallback

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([textContent], { type: 'text/plain' }),
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
        }),
      ]);

      dispatch({ type: 'SET_IS_COPIED', payload: true });
      setTimeout(() => dispatch({ type: 'SET_IS_COPIED', payload: false }), 1000);
      dispatch({ type: 'SET_RES_COPIED_INDEX', payload: index });
    } catch (err) {
      MakeToast('error', 'Failed to copy formatted content');
    }
  };

  // display edit icon on hover of query
  const handleQueryActions = (queryInd: number) => {
    if (!state.showEditQueryActions) {
      dispatch({ type: 'SET_SHOW_QUERY_ACTIONS', payload: true });
      dispatch({ type: 'SET_QUERY_INDEX', payload: queryInd });
    }
  };

  // display save & submit /cancel action
  const handleEditQuery = (value: string) => {
    dispatch({ type: 'SET_SHOW_EDIT_QUERY_ACTIONS', payload: true });
    dispatch({ type: 'SET_UPDATE_QUERY', payload: value });
  };

  // show edit icon on cancel
  const handleCancelEditQuery = () => {
    dispatch({ type: 'SET_SHOW_EDIT_QUERY_ACTIONS', payload: false });
    dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
  };

  // handle textarea content change
  const handleQueryChange = (value: string) => {
    dispatch({ type: 'SET_UPDATE_QUERY', payload: value });
    // Adjust height after state update
    setTimeout(() => {
      adjustTextareaHeight();
    }, 0);
  };

  // on sent query edit
  const handleEditedQuerySend = () => {
    sendButtonRef?.current?.click();
  };

  const handleFileDownload = async (fileName: string, containerId: string, fileId: string) => {
    try {
      const chatData = JSON.parse(localStorage.getItem('chat') || '[]');
      const currentSession = chatData.find(
        (item: { provider_id: number; session_id: string }) => item.provider_id === activeProvider,
      );
      const sessionId = currentSession?.session_id;

      const payload = {
        filename: fileName,
        container_id: containerId,
        file_id: fileId,
        session_id: sessionId,
      };
      const response = await downloadFileInChat(payload);
      let downloadName = fileName;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition && contentDisposition.includes('filename=')) {
        downloadName = contentDisposition.split('filename=')[1].replace(/"/g, '');
      }
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      MakeToast('success', 'File has been Downloaded');
    } catch (error: unknown) {
      const apiError = error as { response?: { status?: number } };
      if (apiError?.response?.status === 401) {
        MakeToast('error', 'Unauthorized');
      } else {
        MakeToast('error', 'Failed to download file');
      }
    }
  };

  const handleResponseInteractiveEvent = (target: HTMLElement) => {
    if (target.classList.contains('download-btn')) {
      const filename = target.getAttribute('data-filename');
      const containerId = target.getAttribute('data-container');
      const fileId = target.getAttribute('data-fileid');
      if (filename && containerId && fileId) {
        handleFileDownload(filename, containerId, fileId);
      }
    }
  };

  const generateFilesHTML = (files: GeneratedFile[] | string | undefined) => {
    let parsedFiles: GeneratedFile[] = [];
    if (typeof files === 'string') {
      try {
        parsedFiles = JSON.parse(files);
      } catch (parseError) {
        return '';
      }
    } else if (Array.isArray(files)) {
      parsedFiles = files;
    }
    if (!Array.isArray(parsedFiles) || parsedFiles.length === 0) return '';
    return `<br><div style="margin-top: 12px; padding: 12px; border: 1px solid #d1d5db; border-radius: 8px; background-color: #f9fafb;"><h4 style="font-size: 14px; font-weight: 500; margin-bottom: 8px;">Generated Files:</h4>${parsedFiles.map((file: GeneratedFile) => `<div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0;"><span style="font-size: 14px; color: #374151;">${file.name}</span><button class="download-btn" data-url="${file.download_url}" data-filename="${file.name}" data-container="${file.container_id}" data-fileid="${file.file_id}" style="padding: 4px 12px; font-size: 12px; background-color: #3b82f6; color: white; border-radius: 4px; border: none; cursor: pointer;">Download</button></div>`).join('')}</div>`;
  };

  // Reusable User Avatar Component
  function UserAvatar() {
    return (
      <div className="flex items-center h-[50px]">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
          <img
            src={AvatarLogo}
            className="px-2 w-10 h-10 rounded-full"
            alt="avatar-logo"
            width={56}
            height={46}
          />
        </div>
      </div>
    );
  }

  const renderMessageContent = (
    chats: {
      role?: string;
      message: string;
      generated_by?: string;
      stream_end?: boolean;
      generatedFiles?: GeneratedFile[];
      generated_files?: GeneratedFile[];
      toolCalls?: ToolCall[];
      type?: string;
    },
    index: number,
  ) => {
    if (state.queryIndex === index && state.showEditQueryActions) {
      return (
        <div className="flex flex-col items-end gap-2" dir="ltr">
          <textarea
            ref={divRef}
            className="edit-query-textarea border border-gray-200 rounded-lg px-4 py-3 w-[630px] text-sm focus:outline-none bg-white resize-none"
            style={{
              minHeight: '2rem',
              maxHeight: '200px',
              overflowY: 'auto',
              direction: 'ltr',
              textAlign: 'left',
              height: 'auto',
            }}
            value={state.updateQuery || ''}
            onChange={(e) => handleQueryChange(e.target.value)}
            onInput={adjustTextareaHeight}
            dir="ltr"
          />
          <div className="flex flex-row items-center gap-[0.5rem]">
            <button
              type="button"
              disabled={state.updateQuery === chats.message}
              className={`font-semibold text-sm p-2 px-4 rounded-lg ${
                state.updateQuery === chats.message
                  ? 'bg-gray-300 text-gray-500'
                  : 'bg-[#0A8BC7] text-white hover:bg-[#247ba4]'
              }`}
              onClick={() =>
                state.queryValue !== '' || state.updateQuery !== ''
                  ? handleEditedQuerySend()
                  : handleCancelEditQuery()
              }
            >
              {SubmitText}
            </button>
            <button
              type="button"
              className="cancel-edit-button text-black border bg-white border-slate-300 text-sm py-[7.5px] px-3 rounded-lg"
              onClick={handleCancelEditQuery}
            >
              {CancelText}
            </button>
          </div>
        </div>
      );
    }

    if (chats?.message?.includes(FILE_UPLOAD_MESSAGE_MARKER)) {
      // Parse file information from message
      const messageLines = chats.message.split('\n');
      const fileIndex = messageLines.findIndex((line) => line.includes(FILE_UPLOAD_MESSAGE_MARKER));

      // Extract query text (everything before file metadata)
      const queryText = fileIndex > 0 ? messageLines.slice(0, fileIndex).join('\n').trim() : '';

      const fileNameLine = messageLines.find((line) => line.includes(FILE_UPLOAD_MESSAGE_MARKER));
      const sizeLine = messageLines.find(
        (line) => line.includes('KB') || line.includes('MB') || line.includes('B'),
      );

      const fileName = fileNameLine?.split(FILE_UPLOAD_MESSAGE_MARKER)[1]?.trim() || '';
      const fileSize = sizeLine?.trim() || '';
      const fileType = chats.type || '';
      const showUploadSpinner = state.isFileUploadPending && index === uploadingFileMessageIndex;

      // Helper function to get file icon based on file type
      const getFileIcon = (type: string) => {
        if (type.startsWith('image/')) {
          return '🖼️';
        }
        if (type === 'application/pdf') {
          return '📄';
        }
        if (
          type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          type === 'text/csv'
        ) {
          return '📊';
        }
        if (
          type === 'application/msword' ||
          type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) {
          return '📝';
        }
        if (type === 'text/plain') {
          return '📄';
        }
        return '📎';
      };

      return (
        <div className="flex items-center justify-center gap-2">
          <div className="flex flex-col gap-2 items-end bg-gray-100 rounded-lg">
            {/* Render file card */}
            <div className="flex items-center justify-start gap-4 px-3 py-2 bg-gray-100 rounded-lg">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="text-2xl flex-shrink-0">{getFileIcon(fileType)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{fileName}</div>
                  <div className="text-xs text-gray-500">
                    {fileSize} • {fileType}
                  </div>
                </div>
              </div>
            </div>
            {/* Render query text if it exists */}
            {queryText && (
              <ChatMarkdown
                content={queryText}
                className={`flex items-center w-full justify-start message-span leading-tight text-[14px] whitespace-normal prose prose-sm ${isNavCollapsed ? 'max-w-3xl' : 'max-w-xl'} py-2 px-3 rounded-xl bg-gray-100 pb-3`}
              />
            )}
          </div>
          {showUploadSpinner && (
            <CircularProgress size={20} sx={{ color: '#0A8BC7', flexShrink: 0 }} />
          )}
          <UserAvatar />
        </div>
      );
    }

    // Render generated files from API response
    const generatedFiles = chats?.generatedFiles || chats?.generated_files;
    if (generatedFiles && Array.isArray(generatedFiles) && generatedFiles.length > 0) {
      return (
        <div className="flex flex-col gap-2">
          {generatedFiles.map((file: GeneratedFile) => (
            <div
              key={file.file_id}
              className="flex flex-row gap-2 items-start justify-start p-[0.4rem] w-full bg-white border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => {
                if (file.container_id && file.file_id && file.name) {
                  handleFileDownload(file.name, file.container_id, file.file_id);
                }
              }}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' || e.key === ' ') &&
                  file.container_id &&
                  file.file_id &&
                  file.name
                ) {
                  e.preventDefault();
                  handleFileDownload(file.name, file.container_id, file.file_id);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Download ${file.name}`}
            >
              <img src={TextFile} className="px-2 text-black rounded-sm" alt="file" />
              <div className="flex flex-col items-center font-medium text-base">
                <pre
                  style={{
                    fontFamily: 'Nunito, sans-serif',
                    marginBottom: '0px',
                  }}
                >
                  {truncateData(file.name, 30)}
                </pre>
              </div>
            </div>
          ))}
        </div>
      );
    }

    const nextTurn = existingChat[index + 1];
    const isTurnStreaming = nextTurn?.role === 'AI' && nextTurn?.isStreaming === true;

    return (
      <UserMessageBubble
        content={chats.message ?? ''}
        showEditButton={Boolean(chats?.message) && !isTurnStreaming}
        onEdit={() => {
          if (chats.message !== undefined) handleEditQuery(chats.message);
        }}
      />
    );
  };

  const lastAIIndex = useMemo(() => {
    for (let i = existingChat.length - 1; i >= 0; i -= 1) {
      if (existingChat[i]?.role === 'AI') {
        return i;
      }
    }
    return -1;
  }, [existingChat]);

  const assistantUiPocRunning = useMemo(
    () => existingChat.some((m) => m.role === 'AI' && m.isStreaming === true),
    [existingChat],
  );

  const assistantUiPocPanel =
    isChatAssistantUiPocEnabled() && existingChat.length > 0 ? (
      <GrigoAssistantUiThreadPoc chatHistory={existingChat} isRunning={assistantUiPocRunning} />
    ) : null;

  const renderChatThreadItem = (chats: DataItem, index: number) => {
    const isLastMessage = index === existingChat.length - 1;
    const isLastHuman = isLastMessage && chats?.role === 'Human';
    const isLastAI = index === lastAIIndex;
    const messageVersions = chats?.role === 'AI' ? getMessageVersions(chats.message) : [];
    const hasMultipleVersions = messageVersions.length > 1;
    const selectedVersionIndex =
      chats?.role === 'AI' ? getSelectedVersionIndex(index, messageVersions) : 0;
    let activeMessage = '';

    if (chats?.role === 'AI') {
      activeMessage = messageVersions[selectedVersionIndex] ?? '';
    } else if (typeof chats?.message === 'string') {
      activeMessage = chats.message;
    }
    return (
      <Fragment key={`chat-row-${index}-${selectedVersionIndex}`}>
        {chats?.role === 'Human' && (
          <div
            ref={isLastHuman ? lastHumanMessageRef : undefined}
            data-role="Human"
            onFocus={() => handleQueryActions(index)}
            onMouseOver={() => handleQueryActions(index)}
            className="message-container chat-turn-user h-auto w-full flex flex-row mb-0 justify-end items-center gap-2"
          >
            <div
              className={`message flex items-center justify-end w-full ${
                state.queryIndex !== index || !state.showEditQueryActions
                  ? 'max-w-[100%]'
                  : 'max-w-max'
              } `}
            >
              {renderMessageContent(
                {
                  role: chats.role,
                  type: chats.type,
                  generated_by: chats.generated_by,
                  stream_end: chats.stream_end,
                  message: typeof chats.message === 'string' ? chats.message : '',
                },
                index,
              )}
            </div>
          </div>
        )}

        {chats?.role === 'AI' && (
          <div ref={isLastAI ? lastAIMessageRef : undefined}>
            <AssistantMessage
              messageIndex={index}
              showAvatar={index === 0 || existingChat[index - 1]?.role === 'Human'}
              activeMessage={activeMessage}
              isStreaming={chats?.isStreaming === true}
              throttleStreamMarkdown={isLastAI}
              isThinking={chats?.isThinking === true}
              thinkingText={chats?.thinkingText}
              thinkingDurationSec={chats?.thinkingDurationSec}
              thinkingEnabled={Boolean(state.thinkingEnabled)}
              thinkingExpanded={expandedThinking.has(index)}
              onToggleThinking={() => {
                const newExpanded = new Set(expandedThinking);
                if (newExpanded.has(index)) {
                  newExpanded.delete(index);
                } else {
                  newExpanded.add(index);
                }
                setExpandedThinking(newExpanded);
              }}
              toolCallsExpanded={expandedToolCalls.has(index)}
              onToggleToolCalls={() => {
                const newExpanded = new Set(expandedToolCalls);
                if (newExpanded.has(index)) {
                  newExpanded.delete(index);
                } else {
                  newExpanded.add(index);
                }
                setExpandedToolCalls(newExpanded);
              }}
              toolCalls={chats?.toolCalls}
              mcpWarnings={chats?.mcpWarnings}
              webSearchUsed={chats?.webSearchUsed}
              webSearchQuery={chats?.webSearchQuery}
              sources={chats?.sources}
              generatedFilesHtml={generateFilesHTML(chats?.generatedFiles) || undefined}
              hasMultipleVersions={hasMultipleVersions}
              selectedVersionIndex={selectedVersionIndex}
              versionCount={messageVersions.length}
              onSelectPrevVersion={() => setSelectedVersionIndex(index, selectedVersionIndex - 1)}
              onSelectNextVersion={() => setSelectedVersionIndex(index, selectedVersionIndex + 1)}
              showResponseActions
              isCopied={Boolean(state.isCopied)}
              resCopiedIndex={state.resCopiedIndex ?? null}
              onCopy={() => {
                if (activeMessage) {
                  handleCopyToClipboard(activeMessage, index);
                }
              }}
              showRetry={Boolean(isLastAI && !chats?.isStreaming && onRetryGeneration)}
              onRetry={onRetryGeneration}
              onInteractiveClick={(e) => {
                handleResponseInteractiveEvent(e.target as HTMLElement);
              }}
              onInteractiveKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleResponseInteractiveEvent(e.target as HTMLElement);
                }
              }}
            />
          </div>
        )}

        {chats?.role === 'Switch-Model' && (
          <div className="flex justify-center w-full my-1">
            <span className="message-span inline-flex items-center gap-1.5 max-w-[min(92%,20rem)] bg-orange-50 text-[#F48B00] px-2.5 py-1 rounded-full border border-[#F48B00]/70 text-[11px] font-medium leading-tight">
              <img src={SwitchIcon} alt="" className="w-3.5 h-3.5 shrink-0 opacity-80" />
              <span className="min-w-0 break-all">{chats?.message}</span>
            </span>
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <div
      className={`scroller flex items-start w-full flex-col gap-[0.25rem] m-0 pb-2 ${
        existingChat?.length > 0 ? 'overflow-x-scroll' : 'overflow-hidden'
      }`}
    >
      {!chatScreenLoading && existingChat?.length > 0 ? (
        <ChatThreadLayout>
          {assistantUiPocPanel}
          {existingChat.map((chats, index) => renderChatThreadItem(chats, index))}
        </ChatThreadLayout>
      ) : null}
    </div>
  );
}

export default ContentContainer;
